export type UserRole = 'ADMIN' | 'EMPLOYEE'

export type RequestType =
  | 'EXCEDENCIA_CORTA_VACACIONES'
  | 'EXCEDENCIA_VOLUNTARIA_ESPECIAL'
  | 'PREMIO_PERMANENCIA_5R'
  | 'LICENCIA_SIN_SUELDO'
  | 'DIAS_LIBRE_ELECCION'
  | 'VACACIONES_FIESTAS_CASEDA'

export type RequestStatus = 'PENDING' | 'APPROVED' | 'DENIED' | 'CANCELLED'

export type WorkCenterType = 'N1' | 'N2' | 'N3' | 'N4_CELULOSA' | 'N4_FIBROSA' | 'PLISADO' | 'IMPRESORA' | 'CALIDAD' | 'LABORATORIO' | 'OTROS' | 'NANOPACK' | 'OTROS_JP'

// Keep old Shift type for backwards compatibility with Employee.shift
export type ShiftCode = 'A' | 'B' | 'C' | 'D' | 'E' | 'R3' | 'R2'
export type Shift = ShiftCode

// Tipo de relevo: 5R (5 relevos), 3R (3 relevos), 2R (2 relevos)
export type ShiftType = 'R5' | 'R3' | 'R2'

// Letra del turno dentro de cada tipo de relevo
export type ShiftLetter = 'A' | 'B' | 'C' | 'D' | 'E'

// Mapeo de turnos válidos por tipo de relevo
export const VALID_SHIFTS_BY_TYPE: Record<ShiftType, ShiftLetter[]> = {
  R5: ['A', 'B', 'C', 'D', 'E'],
  R3: ['A', 'B', 'C'],
  R2: ['A', 'B'],
}

export type ContractType = 'INDEFINIDO' | 'TEMPORAL'

export type CompensationType = 'RECUPERABLE' | 'DESCUENTO_NOMINA'

export interface User {
  id: string
  email: string | null
  name: string
  role: UserRole
  isActive: boolean
  createdAt: string
  employeeId?: string
  employee?: Employee
  mustChangePassword?: boolean
  hasRecoveryEmail?: boolean
  emailVerified?: boolean
  recoveryEmail?: string | null
}

export interface Employee {
  id: string
  employeeId: string
  dni: string
  firstName: string
  lastName: string
  fullName: string
  email: string | null
  positionId: string | null
  position: string | null
  shiftId: string | null
  shift: Shift  // Legacy
  shiftType: ShiftType | null  // Tipo de relevo: R5, R3, R2
  shiftLetter: ShiftLetter | null  // Letra del turno: A, B, C, D, E
  workCenterType: WorkCenterType
  workCenterId: string | null
  workCenterName?: string
  hasStop: boolean
  daysAt5Shifts: number
  startDate: string
  yearsOfService: number
  isActive: boolean
  isPresecadoCelulosa: boolean
  exemptFromLimits: boolean  // Laboratorio, Mantenimiento, Cogeneración
  contractType: ContractType
  isCloseToRetirement: boolean  // Próximo a jubilación (para Premio Permanencia 5R)
  partialRetirementDate: string | null  // Fecha de jubilación parcial (para PP5R)
  birthDate: string | null  // Fecha de nacimiento
  hadExcedenciaCorta2Years: boolean  // Excedencia Corta en 2 años anteriores
  hadExcedenciaEspecial2Years: boolean  // Excedencia Voluntaria Especial en 2 años anteriores
  daysUsedPP5R: number  // Días ya disfrutados de Premio Permanencia 5R
  canRequestPP5R: boolean  // ¿Puede solicitar PP5R? (dato del cliente)
  arrancadaDates: string | null  // Fechas de arrancada
  hasUser: boolean
  unreadNotifications?: number
  emailVerified?: boolean
}

export interface Position {
  id: string
  name: string
  isActive: boolean
  employeeCount?: number
  createdAt?: string
  updatedAt?: string
}

// New Shift model interface for CRUD operations
export interface ShiftModel {
  id: string
  code: string
  name: string
  description: string | null
  type: '5R' | '3R'
  isActive: boolean
  employeeCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface WorkCenter {
  id: string
  type: WorkCenterType
  name: string
  fixedStaff: number
  maxConcurrent: number
  maxPerPosition: number
  maxPerDay: number
  isActive: boolean
  employeeCount?: number
  createdAt: string
  updatedAt: string
}

export interface WorkCentersListResponse {
  workCenters: WorkCenter[]
  total: number
}

export type NotificationType =
  | 'NEW_NOTE'
  | 'NOTE_UPDATED'
  | 'REQUEST_APPROVED'
  | 'REQUEST_DENIED'
  | 'REQUEST_CANCELLED'
  | 'SYSTEM'

export interface EmployeeNote {
  id: string
  employeeId: string
  title: string
  content: string
  isImportant: boolean
  createdAt: string
  createdBy: {
    id: string
    name: string
  }
}

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  isRead: boolean
  relatedId: string | null
  createdAt: string
}

export interface LicenseRequest {
  id: string
  employeeDbId: string  // CUID - database ID of the employee
  employeeId: string    // Internal employee code (e.g., "EMP001")
  employeeDni: string   // Employee DNI
  employeeName: string
  employeePosition: string | null
  employeeWorkCenter: WorkCenterType
  employeeShift: Shift
  requestType: RequestType
  requestTypeName: string
  startDate: string
  endDate: string
  secondDate: string | null         // Segunda fecha para DLE con días no consecutivos
  daysRequested: number
  status: RequestStatus
  statusName: string
  requestedAt: string
  processedAt: string | null
  processedById: string | null
  processedByName: string | null  // Nombre del admin que procesó la solicitud
  denialReason: string | null
  cancelledAt: string | null
  cancelledById: string | null
  cancelledByName: string | null  // Nombre del admin que canceló la solicitud
  cancellationReason: string | null
  isExtension: boolean
  notes: string | null
  attachmentUrl: string | null      // URL del documento adjunto
  attachmentName: string | null     // Nombre del archivo adjunto
  requiresManualValidation: boolean // Para Vacaciones Fiestas Cáseda
  compensationType: CompensationType | null // Solo para DLE: RECUPERABLE o DESCUENTO_NOMINA
  compensationTypeName: string | null       // Nombre legible del tipo de compensación
}

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
  adminOverrideApplied?: boolean
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface ApiResponse<T> {
  success: boolean
  data: T
}

export const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  EXCEDENCIA_CORTA_VACACIONES: 'Excedencia de corta duración para vacaciones',
  EXCEDENCIA_VOLUNTARIA_ESPECIAL: 'Excedencia voluntaria especial',
  PREMIO_PERMANENCIA_5R: 'Premio permanencia sistema a 5R',
  LICENCIA_SIN_SUELDO: 'Licencia sin sueldo',
  DIAS_LIBRE_ELECCION: 'Días de libre elección',
  VACACIONES_FIESTAS_CASEDA: 'Vacaciones fiestas Cáseda',
}

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  DENIED: 'Denegada',
  CANCELLED: 'Cancelada',
}

export const WORK_CENTER_LABELS: Record<WorkCenterType, string> = {
  N1: 'Nave 1',
  N2: 'Nave 2',
  N3: 'Nave 3',
  N4_CELULOSA: 'Nave 4 - Celulosa',
  N4_FIBROSA: 'Nave 4 - Fibrosa',
  PLISADO: 'Plisado',
  IMPRESORA: 'Impresora',
  CALIDAD: 'Calidad',
  LABORATORIO: 'Laboratorio',
  OTROS: 'Otros',
  NANOPACK: 'Nanopack',
  OTROS_JP: 'Otros JP',
}

export const SHIFT_LABELS: Record<Shift, string> = {
  A: 'Turno A',
  B: 'Turno B',
  C: 'Turno C',
  D: 'Turno D',
  E: 'Turno E',
  R3: 'Turno 3R (Plisado)',
  R2: 'Turno 2R',
}

export const SHIFT_TYPE_LABELS: Record<ShiftType, string> = {
  R5: '5 Relevos (5R)',
  R3: '3 Relevos (3R)',
  R2: '2 Relevos (2R)',
}

export const SHIFT_LETTER_LABELS: Record<ShiftLetter, string> = {
  A: 'Turno A',
  B: 'Turno B',
  C: 'Turno C',
  D: 'Turno D',
  E: 'Turno E',
}

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  INDEFINIDO: 'Indefinido',
  TEMPORAL: 'Temporal',
}

export const COMPENSATION_TYPE_LABELS: Record<CompensationType, string> = {
  RECUPERABLE: 'Recuperable',
  DESCUENTO_NOMINA: 'Descuento en nómina',
}

// ===== REGLAS =====
export type RuleCategory =
  | 'DURATION'
  | 'ADVANCE_NOTICE'
  | 'CONCURRENCY'
  | 'BLOCKED_PERIOD'
  | 'ELIGIBILITY'
  | 'DEADLINE'
  | 'WORK_CENTER_LIMIT'

export interface RuleValues {
  minDays?: number
  maxDays?: number
  exactDays?: number
  allowedDays?: number[]
  maxPercent?: number
  minWorkers?: number
  maxPerPosition?: number
  maxConcurrent?: number
  minDaysAt5Shifts?: number
  minDaysLast10Years?: number
  minYearsOfService?: number
  requiresAt5Shifts?: boolean
  notEnjoyed2Years?: boolean
  month?: number
  day?: number
  alternativeDays?: number
  maxPerTurnPosition?: number
  maxPerDay?: number
  extraForPresecado?: number
  blockedPeriodTypes?: string[]
  allowedOnly?: boolean
}

export interface RuleConditions {
  hasStop?: boolean
  isPresecadoCelulosa?: boolean
  shift?: string
  daysRequested?: number
  isExtension?: boolean
}

export interface Rule {
  id: string
  code: string
  name: string
  description: string | null
  category: RuleCategory
  requestType: RequestType | null
  workCenter: WorkCenterType | null
  values: RuleValues
  conditions: RuleConditions | null
  errorMessage: string | null
  priority: number
  sortOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface RulesListResponse {
  rules: Rule[]
  total: number
  grouped?: Record<string, Rule[]>
}

export const RULE_CATEGORY_LABELS: Record<RuleCategory, string> = {
  DURATION: 'Duración',
  ADVANCE_NOTICE: 'Antelación',
  CONCURRENCY: 'Límites concurrentes',
  BLOCKED_PERIOD: 'Periodos bloqueados',
  ELIGIBILITY: 'Elegibilidad',
  DEADLINE: 'Fecha límite',
  WORK_CENTER_LIMIT: 'Límite por centro',
}
